import {ALMOST_OUT_THRESHOLD, APPROACHING_THRESHOLD} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {CopilotPlan, UserLevelBudgetState} from '@github-ui/copilot-chat-types'
import {useEntitlement} from '@github-ui/copilot-entitlement/EntitlementContext'
import {useCallback, useSyncExternalStore} from 'react'

// Constructed once at module scope (Intl.DateTimeFormat construction is
// expensive, particularly on Safari).
const quotaResetFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
})

export function formatQuotaResetLabel(date: string | undefined | null): string {
  if (!date) return ''

  // Accept both date-only strings ("2022-02-01") and full datetime strings
  // ("2022-02-01T00:00:00.000Z"). Date-only strings are treated as UTC midnight
  // and then formatted in the user's local timezone.
  const isoString = date.includes('T') ? date : `${date}T00:00:00.000Z`
  const resetTime = new Date(isoString)
  if (Number.isNaN(resetTime.getTime())) return ''

  return `on ${quotaResetFormatter.format(resetTime)}`
}

/**
 * Scenario variants supported by <QuotaBanner />.
 *
 * - `freeApproaching`: free user has used >75% of their quota.
 * - `freeAlmostOut`: free user has used >90% of their quota.
 * - `freeExceeded`: free user has hit their TBB limit – usage is paused.
 * - `paidApproaching`: paid user has used >75% of their AI credits.
 * - `paidAlmostOut`: paid user has used >90% of their AI credits.
 * - `paidExceeded`: paid user has hit their TBB limit and either has no
 *   overage enabled or has burned through their overage budget – usage is
 *   paused.
 * - `overageApproaching`: paid user has used >75% of their overage budget.
 * - `overageAlmostOut`: paid user has used >90% of their overage budget.
 * - `overageExceeded`: paid user has hit their overage budget – usage is paused.
 * - `overageActive`: paid user has hit their usage limit but has
 *   overage enabled with budget remaining – usage continues on overage
 *   spend. Informational, non-blocking.
 * - `userLevelBudgetProgress`: CB/CE user has ≤25% of their monthly
 *   user-level budget remaining. Informational, dismissable.
 * - `userLevelBudgetReached`: CB/CE user has exhausted their monthly
 *   user-level budget – usage is paused.
 * - `userLevelBudgetUsageBlocked`: CB/CE user has unlimited user-level
 *   quota but the org-level pool is exhausted – usage is blocked.
 * - `licenseProvisioning`: the user's Copilot license is still being issued
 *   or upgraded by the licensing backend (e.g. copilot-limiter's cache
 *   hasn't caught up with a recent signup or plan change). Surfaced as a
 *   non-blocking informational banner so users don't see misleading
 *   "quota exceeded" / "not authorized" errors during the propagation window.
 */
export type QuotaBannerVariant =
  | 'freeApproaching'
  | 'freeAlmostOut'
  | 'freeExceeded'
  | 'paidApproaching'
  | 'paidAlmostOut'
  | 'paidExceeded'
  | 'overageApproaching'
  | 'overageAlmostOut'
  | 'overageExceeded'
  | 'overageActive'
  | 'userLevelBudgetProgress'
  | 'userLevelBudgetAlmostReached'
  | 'userLevelBudgetReached'
  | 'userLevelBudgetUsageBlocked'
  | 'licenseProvisioning'

export interface UseQuotaBannerResult {
  /** Whether the banner should be rendered. */
  show: boolean
  /** The scenario to render. Always `null` when `show` is `false`. */
  variant: QuotaBannerVariant | null
  /** Current usage percentage of the relevant quota, for display in approaching-limit scenarios. */
  quotaUsage?: number | null
  /** Human-friendly reset label for the currently-relevant quota. */
  resetLabel: string
  /** Whether the banner should render an "Upgrade plan" button. */
  showUpgradeButton: boolean
  /** Whether the banner should render an "Enable/Edit overage" button. */
  showOverageButton: boolean
  /**
   * Whether the banner should render the single "Increase your budget" CTA for
   * standalone Copilot Individual subscribers whose personal budget is
   * exhausted (in-product-messaging#489). Gated in the component behind
   * `ubbNewCtaDestinations`; when the flag is off the legacy upgrade/overage
   * layout is rendered instead.
   */
  showIncreaseBudgetButton: boolean
  /**
   * Whether this banner represents a state that blocks chat input (e.g. usage
   * paused). Callers can use this to disable the input area in addition to
   * showing the banner. Informational/approaching-limit banners are not
   * blocking.
   */
  isBlocking: boolean
  /** Whether this banner can be dismissed by the user. */
  isDismissable: boolean
  /** When set, the banner is dismissable and should call this on dismiss. */
  onDismiss?: (this: void) => void
}

const EMPTY_RESULT: UseQuotaBannerResult = {
  show: false,
  variant: null,
  resetLabel: '',
  showUpgradeButton: false,
  showOverageButton: false,
  showIncreaseBudgetButton: false,
  isBlocking: false,
  isDismissable: false,
  quotaUsage: null,
}

type EntitlementSnapshot = ReturnType<typeof useEntitlement>

function banner(variant: QuotaBannerVariant, overrides: Partial<UseQuotaBannerResult> = {}): UseQuotaBannerResult {
  return {
    show: true,
    variant,
    quotaUsage: null,
    resetLabel: '',
    showUpgradeButton: false,
    showOverageButton: false,
    showIncreaseBudgetButton: false,
    isBlocking: false,
    isDismissable: false,
    ...overrides,
  }
}

/**
 * Pure helper that derives the banner result from entitlement values.
 * Extracted so the hook can call it after all React hooks are declared.
 */
function resolveQuotaBannerResult(e: EntitlementSnapshot): UseQuotaBannerResult {
  const {
    isLicensedLimited,
    overagesRemaining,
    overagesExceeded,
    overagesEnabled,
    quotaExceeded,
    quotaRemaining,
    quotaResetAt,
    canUpgradePlan,
    plan,
    userLevelBudgetEnabled,
    userLevelBudgetState,
    userLevelBudgetPercentRemaining,
    licenseProvisioning,
  } = e

  // Short-circuit when the licensing backend reports that the user's license
  // is still being issued or upgraded. This must precede every other branch
  // so transient empty-quota / wrong-license-type states don't fall through
  // into the misleading `freeExceeded` / `paidExceeded` banners.
  if (licenseProvisioning) {
    return banner('licenseProvisioning')
  }

  const isIndividualPaid =
    plan === CopilotPlan.IndividualPro ||
    plan === CopilotPlan.IndividualProPlus ||
    plan === CopilotPlan.IndividualMax ||
    plan === CopilotPlan.IndividualStudent

  const isOverageBudgetExhausted = overagesEnabled && overagesExceeded && quotaExceeded
  const isOnOverageSpend = overagesEnabled && !overagesExceeded && quotaExceeded
  const hasOverageBudgetData = overagesEnabled && quotaExceeded && overagesRemaining !== undefined

  const resetLabel = formatQuotaResetLabel(quotaResetAt)
  const quotaUsage = 100 - (quotaRemaining ?? 0)
  const overageUsage = 100 - (overagesRemaining ?? 0)

  // CB/CE token-based-billing users: prefer the user-level budget banner
  // (computed from premiumInteractionsQuota) over free/paid surfaces.
  if (userLevelBudgetEnabled) {
    switch (userLevelBudgetState) {
      case UserLevelBudgetState.BudgetReached:
        return banner('userLevelBudgetReached', {resetLabel, isBlocking: true})
      case UserLevelBudgetState.UsageBlocked:
        return banner('userLevelBudgetUsageBlocked', {isBlocking: true})
      case UserLevelBudgetState.BudgetAlmostReached:
        return banner('userLevelBudgetAlmostReached', {
          quotaUsage: 100 - (userLevelBudgetPercentRemaining ?? 0),
          isDismissable: true,
        })
      case UserLevelBudgetState.BudgetProgress:
        return banner('userLevelBudgetProgress', {
          quotaUsage: 100 - (userLevelBudgetPercentRemaining ?? 0),
          isDismissable: true,
        })
    }
  }

  // Free users
  if (isLicensedLimited) {
    if (quotaExceeded) {
      return banner('freeExceeded', {quotaUsage, resetLabel, showUpgradeButton: canUpgradePlan, isBlocking: true})
    }

    const remaining = quotaRemaining ?? Infinity
    if (remaining > 0 && remaining <= ALMOST_OUT_THRESHOLD) {
      return banner('freeAlmostOut', {quotaUsage, resetLabel, showUpgradeButton: canUpgradePlan, isDismissable: true})
    }
    if (remaining > 0 && remaining <= APPROACHING_THRESHOLD) {
      return banner('freeApproaching', {quotaUsage, resetLabel, showUpgradeButton: canUpgradePlan, isDismissable: true})
    }

    return EMPTY_RESULT
  }

  // Paid individual users (Pro/Pro+/Max/Student)
  if (isIndividualPaid) {
    const quotaExceededWithoutOverage = !overagesEnabled && quotaExceeded

    if (quotaExceededWithoutOverage) {
      return banner('paidExceeded', {
        quotaUsage,
        resetLabel,
        showUpgradeButton: canUpgradePlan,
        showOverageButton: true,
        showIncreaseBudgetButton: true,
        isBlocking: true,
      })
    }

    if (isOverageBudgetExhausted) {
      return banner('overageExceeded', {
        quotaUsage: overageUsage,
        resetLabel: '',
        showUpgradeButton: canUpgradePlan,
        showOverageButton: true,
        showIncreaseBudgetButton: true,
        isBlocking: true,
      })
    }

    // Approaching included quota — two tiers. Always fires on the threshold,
    // including when overages are enabled, so paid users aren't surprised
    // when they transition from included quota into overage spend. Hide the
    // "Enable additional usage" CTA when overages are already enabled.
    const paidRemaining = quotaRemaining ?? Infinity
    if (paidRemaining > 0 && paidRemaining <= ALMOST_OUT_THRESHOLD) {
      return banner('paidAlmostOut', {
        quotaUsage,
        resetLabel,
        showOverageButton: !overagesEnabled,
        isDismissable: true,
      })
    }
    if (paidRemaining > 0 && paidRemaining <= APPROACHING_THRESHOLD) {
      return banner('paidApproaching', {
        quotaUsage,
        resetLabel,
        showOverageButton: !overagesEnabled,
        isDismissable: true,
      })
    }

    // On overage, approaching budget limit — two tiers
    if (hasOverageBudgetData) {
      if (overagesRemaining > 0 && overagesRemaining <= ALMOST_OUT_THRESHOLD) {
        return banner('overageAlmostOut', {
          quotaUsage: overageUsage,
          resetLabel: '',
          showOverageButton: true,
          isDismissable: true,
        })
      }
      if (overagesRemaining > 0 && overagesRemaining <= APPROACHING_THRESHOLD) {
        return banner('overageApproaching', {
          quotaUsage: overageUsage,
          resetLabel: '',
          showOverageButton: true,
          isDismissable: true,
        })
      }
    }

    // On overage spend with budget remaining
    if (isOnOverageSpend) {
      return banner('overageActive', {quotaUsage, resetLabel, isDismissable: true})
    }

    return EMPTY_RESULT
  }

  return EMPTY_RESULT
}

// Custom event dispatched (same-window) whenever a quota banner flag is written to localStorage.
const QUOTA_BANNER_DISMISSED_EVENT = 'copilot-quota-banner-dismissed'

function subscribeToQuotaBannerDismissal(callback: () => void) {
  window.addEventListener(QUOTA_BANNER_DISMISSED_EVENT, callback)
  return () => window.removeEventListener(QUOTA_BANNER_DISMISSED_EVENT, callback)
}

/**
 * Computes the variant + visibility for the shared <QuotaBanner /> used by TBB.
 *
 * Returns `{ show: false, variant: null }` when the TBB feature flag is
 * disabled, so callers can safely fall back to the legacy (non-TBB) banners.
 *
 * For non-blocking variants (`overageApproaching`, `overageAlmostOut`, `overageActive`,
 * `onDismiss` persists the dismissal in `localStorage` via `copilotLocalStorage`
 * so the banner stays hidden on reload. `useSyncExternalStore` subscribes to
 * localStorage changes via a custom event so the banner correctly hides both
 * when the user explicitly dismisses it and when the variant transitions into
 * one that was already dismissed in a prior session.
 */
export function useQuotaBanner(): UseQuotaBannerResult {
  const entitlement = useEntitlement()
  // licenseProvisioning is independent of token-based billing: it represents
  // a transient license-issuing state from the licensing backend that should
  // surface regardless of whether the user is on a TBB plan.
  const quotaBanner =
    entitlement.licenseProvisioning || entitlement.tokenBasedBillingEnabled
      ? resolveQuotaBannerResult(entitlement)
      : EMPTY_RESULT

  const getSnapshot = useCallback(
    () => (quotaBanner.variant ? copilotLocalStorage.getQuotaBannerFlag(quotaBanner.variant) : null),
    [quotaBanner.variant],
  )

  const isDismissed = useSyncExternalStore(subscribeToQuotaBannerDismissal, getSnapshot, () => null)

  const handleDismiss = useCallback(() => {
    if (quotaBanner.variant) {
      copilotLocalStorage.setQuotaBannerFlag(quotaBanner.variant, true)
      window.dispatchEvent(new Event(QUOTA_BANNER_DISMISSED_EVENT))
    }
  }, [quotaBanner.variant])

  return {
    ...quotaBanner,
    show: quotaBanner.show && !isDismissed,
    onDismiss: quotaBanner.isDismissable ? handleDismiss : undefined,
  }
}
